/**
 * ============================================
 * COCKROACHDB SECTION
 * ============================================
 * 
 * Shows:
 *   - Cluster name
 *   - Concurrent Users (CCU) graph with duration selector
 *   - Player platform breakdown
 *   - Cross-platform combinations
 */

import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, CartesianGrid, ReferenceLine
} from 'recharts';
import { 
  Database, RefreshCw, Users, Activity
} from 'lucide-react';
import { getPlatformInfo, getPlatformComboLabel } from '@/config';
import type { PlatformCombination, CCUDataPoint } from '@/hooks';
import { MARKER_CHART_MARGIN, markerLabel } from '@/components/common/ChartMarkers';

function formatCount(count: number): string {
  if (count >= 1000000) {
    const value = count / 1000000;
    return value % 1 === 0 ? `${value}M` : `${value.toFixed(1)}M`;
  }
  if (count >= 1000) {
    const value = count / 1000;
    return value % 1 === 0 ? `${value}K` : `${value.toFixed(1)}K`;
  }
  return count.toString();
}

const tooltipStyle = {
  backgroundColor: '#1f2937',
  border: '1px solid #374151',
  borderRadius: '8px',
  padding: '8px 12px',
  color: '#f9fafb',
};

// Colors for combinations
const COMBO_COLORS = [
  '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#22c55e',
  '#06b6d4', '#84cc16', '#f43f5e', '#6366f1', '#14b8a6',
];

// CCU threshold levels for reference lines
const CCU_THRESHOLDS = [100, 250, 500, 1500, 2500, 4000, 5000, 7500, 9000, 10000];

// Duration options for CCU chart
const DURATION_OPTIONS = [
  { value: '1h', label: '1H' },
  { value: '4h', label: '4H' },
  { value: '8h', label: '8H' },
  { value: '2d', label: '2D' },
  { value: '4d', label: '4D' },
  { value: '15d', label: '15D' },
];

// Y-axis scale options for CCU chart - simplified
const SCALE_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: '1000', label: '1K' },
  { value: '5000', label: '5K' },
  { value: '10000', label: '10K' },
];

interface CockroachDBSectionProps {
  clusterName?: string;
  totalPlayers: number;
  platformBreakdown: Record<string, number>;
  platformCombinations: PlatformCombination[];
  ccuData: CCUDataPoint[];
  ccuDuration: string;
  onCCUDurationChange: (duration: string) => void;
  currentCCU: number;
  isLoading: boolean;
}

export function CockroachDBSection({ 
  clusterName,
  totalPlayers, 
  platformBreakdown,
  platformCombinations,
  ccuData,
  ccuDuration,
  onCCUDurationChange,
  currentCCU,
  isLoading 
}: CockroachDBSectionProps) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [yAxisScale, setYAxisScale] = useState<string>('auto');

  const handleRefresh = async () => {
    setIsRefreshing(true);
    onCCUDurationChange(ccuDuration);
    setTimeout(() => setIsRefreshing(false), 500);
  };

  // Prepare platform breakdown data for bar chart
  const platformData = Object.entries(platformBreakdown).map(([platformId, count]) => {
    const info = getPlatformInfo(platformId);
    return {
      name: info.name,
      shortName: info.shortName,
      count,
      color: info.color,
    };
  }).sort((a, b) => b.count - a.count);

  // Prepare combination data with labels
  const comboData = platformCombinations.slice(0, 10).map((combo, index) => {
    // Parse the combo key (e.g., "0-5" -> [0, 5])
    const platformIds = combo.key.split('-').map(Number);
    const label = getPlatformComboLabel(platformIds);
    
    return {
      key: combo.key,
      label,
      count: combo.count,
      color: COMBO_COLORS[index % COMBO_COLORS.length],
    };
  });

  const formatChartTime = (timestamp: number) => {
    const date = new Date(timestamp);
    // Show different format based on duration
    if (ccuDuration === '1h' || ccuDuration === '4h' || ccuDuration === '8h') {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Calculate max CCU for Y-axis domain based on scale selection
  const getYAxisMax = () => {
    if (yAxisScale !== 'auto') {
      return parseInt(yAxisScale, 10);
    }
    // Auto: calculate based on data, round up to nearest divisor of 5
    const maxCCU = Math.max(...ccuData.map(d => d.value), currentCCU, 1);
    
    // Find the closest upper bound that's a nice round number divisible by 5
    if (maxCCU <= 5) return 5;
    if (maxCCU <= 10) return 10;
    if (maxCCU <= 25) return 25;
    if (maxCCU <= 50) return 50;
    if (maxCCU <= 100) return 100;
    if (maxCCU <= 250) return 250;
    if (maxCCU <= 500) return 500;
    if (maxCCU <= 1000) return 1000;
    if (maxCCU <= 2500) return 2500;
    if (maxCCU <= 5000) return 5000;
    if (maxCCU <= 10000) return 10000;
    // For larger values, round up to nearest 5000
    return Math.ceil(maxCCU / 5000) * 5000;
  };
  
  const yAxisMax = getYAxisMax();

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 flex-wrap">
            <Database className="h-5 w-5 flex-shrink-0" />
            <span>CockroachDB</span>
            {clusterName && (
              <span className="text-xs sm:text-sm font-normal text-muted-foreground bg-muted/50 px-2 py-0.5 rounded truncate max-w-[150px] sm:max-w-none">
                {clusterName}
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading || isRefreshing}
              className="h-8 px-3"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {isLoading && totalPlayers === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            <span className="ml-3 text-muted-foreground">Loading CockroachDB data...</span>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Player Statistics - Platform Breakdown (MOVED TO TOP) */}
            {platformData.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Player Statistics
                  </p>
                  <p className="text-2xl font-bold">{formatCount(totalPlayers)}</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Platform Breakdown Bar Chart */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Platform Distribution</p>
                    <div className="h-[180px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={platformData} layout="vertical">
                          <XAxis type="number" hide />
                          <YAxis 
                            type="category" 
                            dataKey="name" 
                            width={80}
                            tick={{ fontSize: 12 }}
                          />
                          <Tooltip 
                            contentStyle={tooltipStyle}
                            formatter={(value: number) => [formatCount(value), 'Players']}
                          />
                          <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                            {platformData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Platform Breakdown List */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Platform Breakdown</p>
                    <div className="space-y-1 max-h-[180px] overflow-y-auto">
                      {platformData.map((platform) => (
                        <div
                          key={platform.shortName || platform.name}
                          className="flex items-center justify-between text-xs p-2 rounded bg-muted/20"
                        >
                          <div className="flex items-center gap-2">
                            <span 
                              className="w-3 h-3 rounded-full flex-shrink-0" 
                              style={{ backgroundColor: platform.color }}
                            />
                            <span className="truncate">{platform.name}</span>
                          </div>
                          <span className="font-bold ml-2">{formatCount(platform.count)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Cross-Platform Combinations */}
            {comboData.length > 0 && (
              <>
                <div className="border-t border-border" />
                
                <div>
                  <p className="text-sm font-medium flex items-center gap-2 mb-3">
                    <Users className="h-4 w-4" />
                    Cross-Platform Combinations (Top 10)
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {comboData.map((combo) => (
                      <div
                        key={combo.key}
                        className="flex items-center justify-between text-xs p-2 rounded bg-muted/20"
                      >
                        <div className="flex items-center gap-2">
                          <span 
                            className="w-3 h-3 rounded-full flex-shrink-0" 
                            style={{ backgroundColor: combo.color }}
                          />
                          <span className="truncate">{combo.label}</span>
                        </div>
                        <span className="font-bold ml-2">{formatCount(combo.count)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Concurrent Users Graph (REPLACED SQL CPU) */}
            {(platformData.length > 0 || comboData.length > 0) && (
              <div className="border-t border-border" />
            )}
            
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Concurrent Users
                  <span className="text-xs text-muted-foreground font-normal">
                    (Current: {formatCount(currentCCU)})
                  </span>
                </p>
                
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-6">
                  {/* Y-axis scale selector */}
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-xs text-muted-foreground mr-1">Scale:</span>
                    {SCALE_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setYAxisScale(option.value)}
                        className={`px-2 py-1 text-xs rounded transition-colors ${
                          yAxisScale === option.value
                            ? 'bg-orange-500 text-white'
                            : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  
                  {/* Duration selector */}
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-xs text-muted-foreground mr-1">Time:</span>
                    {DURATION_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => onCCUDurationChange(option.value)}
                        className={`px-2 py-1 text-xs rounded transition-colors ${
                          ccuDuration === option.value
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="h-[200px]">
                {ccuData && ccuData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={ccuData} margin={{ ...MARKER_CHART_MARGIN, right: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis 
                        dataKey="timestamp" 
                        tickFormatter={formatChartTime}
                        tick={{ fontSize: 10 }}
                        stroke="#6b7280"
                      />
                      <YAxis 
                        domain={[0, yAxisMax]}
                        tick={{ fontSize: 10 }}
                        stroke="#6b7280"
                        tickFormatter={(value) => formatCount(value)}
                      />
                      <Tooltip 
                        contentStyle={tooltipStyle}
                        labelFormatter={(timestamp) => new Date(timestamp as number).toLocaleString()}
                        formatter={(value: number) => [formatCount(value), 'CCU']}
                      />
                      {/* Reference lines for CCU thresholds */}
                      {CCU_THRESHOLDS.filter(t => t <= yAxisMax).map((threshold) => (
                        <ReferenceLine
                          key={threshold}
                          y={threshold}
                          stroke="#f59e0b"
                          strokeDasharray="3 3"
                          strokeOpacity={0.5}
                        />
                      ))}
                      {/* ATH marker */}
                      {(() => {
                        const ath = Math.max(...ccuData.map(d => d.value));
                        return ath > 0 ? (
                          <ReferenceLine y={ath} stroke="#ef4444" strokeDasharray="4 3" strokeWidth={1.5}
                            label={markerLabel(`ATH ${formatCount(ath)}`, '#ef4444', 'above')} />
                        ) : null;
                      })()}
                      {/* Current marker */}
                      {ccuData.length > 0 && (
                        <ReferenceLine y={ccuData[ccuData.length - 1].value} stroke="#22c55e" strokeDasharray="4 3" strokeWidth={1.5}
                          label={markerLabel(`Now ${formatCount(ccuData[ccuData.length - 1].value)}`, '#22c55e', 'below')} />
                      )}
                      <Line
                        type="monotone"
                        dataKey="value"
                        name="CCU"
                        stroke="#f97316"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                    No CCU data available
                  </div>
                )}
              </div>
            </div>

            {/* Loading state when data hasn't arrived yet */}
            {platformData.length === 0 && totalPlayers === 0 && (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                <span className="ml-2 text-xs text-muted-foreground">Loading player data...</span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
